import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MagnifyingGlassIcon, PlusIcon } from "@heroicons/react/24/outline";
import PageHeader from "../components/layout/PageHeader";
import { Input } from "../components/ui/Input";
import { useActivities } from "../hooks/useActivities";
import { useAuth } from "../hooks/useAuth";
import type { Activity } from "../types";

function ActivityCard({ activity }: { activity: Activity }) {
  const navigate = useNavigate();
  return (
    <div
      className="camp-card overflow-hidden cursor-pointer group"
      onClick={() => navigate(`/activities/${activity.id}`)}
    >
      {/* Badge / photo */}
      <div className="photo-placeholder h-36 relative overflow-hidden">
        {activity.thumbnailUrl && (
          <img
            src={activity.thumbnailUrl}
            alt={activity.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-stone-900 group-hover:text-forest-700 transition-colors">
          {activity.name}
        </h3>
        {activity.location && (
          <p className="text-xs text-stone-400 mt-0.5">{activity.location}</p>
        )}
        {activity.description && (
          <p className="text-sm text-stone-500 mt-2 line-clamp-2">{activity.description}</p>
        )}
        <div className="mt-3">
          <span className="text-xs text-stone-400">
            {activity.achievementCredits?.length ?? 0} credits
          </span>
        </div>
      </div>
    </div>
  );
}

export default function Activities() {
  const { activities, loading } = useActivities();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "director";
  const [search, setSearch] = useState("");

  const filtered = activities.filter((act) => {
    return (
      !search ||
      act.name.toLowerCase().includes(search.toLowerCase()) ||
      act.description?.toLowerCase().includes(search.toLowerCase())
    );
  });

  return (
    <div>
      <PageHeader
        title="Activities"
        actions={
          isAdmin ? (
            <Link
              to="/admin/activities"
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-forest-900 font-semibold text-sm px-4 py-2 rounded-lg shadow-sm transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Add Activity
            </Link>
          ) : undefined
        }
      />

      <div className="p-6 lg:p-8 space-y-6">
        {/* Search */}
        <div className="sm:w-72">
          <Input
            placeholder="Search activities…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
          />
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="camp-card overflow-hidden animate-pulse">
                <div className="h-36 bg-stone-200" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-stone-200 rounded w-3/4" />
                  <div className="h-3 bg-stone-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-stone-400">No activities found.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((act) => (
              <ActivityCard key={act.id} activity={act} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
